import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  fetchWritingExercise,
  evaluateWritingSentence,
  saveExerciseProgress,
  loadExerciseProgress,
  recordExerciseAttempt
} from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';
import Loading from '../Loading';
import Error from '../Error';
import ExerciseLayout from '../shared/ExerciseLayout';
import ExerciseCard from '../shared/ExerciseCard';
import XPBadge from '../shared/XPBadge';
import TranslatableText from '../shared/TranslatableText';

const WritingExercise = forwardRef(({ topic = 'family_hobbies', onBack }, ref) => {
  const [exerciseData, setExerciseData] = useState(null);
  const [sentences, setSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [errorSentences, setErrorSentences] = useState(null);
  const [sentenceEvaluations, setSentenceEvaluations] = useState({});
  const [evaluatingIndex, setEvaluatingIndex] = useState(null);
  const { stop: stopTimer } = useExerciseTimer();
  const [showExamples, setShowExamples] = useState(false);
  const [engagementData, setEngagementData] = useState(null);

  // Expose saveProgress method to parent component
  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      try {
        await saveExerciseProgress(topic, 'writing', {
          sentences,
          showResults,
          evaluation,
          errorSentences,
          sentenceEvaluations
        });
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  // Fetch exercise from API on component mount
  useEffect(() => {
    const loadExercise = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchWritingExercise({ topic });
        setExerciseData(data);
        setSentences(Array(data.min_sentences || 5).fill(''));

      } catch (err) {
        console.error('Error fetching writing exercise:', err);
        setError(err.message || 'Failed to load exercise');
      } finally {
        setLoading(false);
      }
    };

    loadExercise();
  }, [topic]);

  // Load saved progress after exercise is loaded
  useEffect(() => {
    const loadProgress = async () => {
      if (!loading && exerciseData) {
        try {
          const savedProgress = await loadExerciseProgress(topic, 'writing');
          if (savedProgress && savedProgress.progress_data) {
            if (savedProgress.progress_data.sentences) {
              setSentences(savedProgress.progress_data.sentences);
            }
            if (savedProgress.progress_data.evaluation) {
              setEvaluation(savedProgress.progress_data.evaluation);
            }
            if (savedProgress.progress_data.showResults) {
              setShowResults(true);
            }
            if (savedProgress.progress_data.errorSentences) {
              setErrorSentences(savedProgress.progress_data.errorSentences);
            }
            if (savedProgress.progress_data.sentenceEvaluations) {
              setSentenceEvaluations(savedProgress.progress_data.sentenceEvaluations);
            }
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [loading, exerciseData, topic]);

  // Count filled sentences
  const filledCount = sentences.filter(s => s.trim().length > 0).length;
  const minSentences = exerciseData?.min_sentences || 10;
  const allSentencesFilled = filledCount === minSentences;
  const emptySentenceNumbers = sentences
    .map((s, i) => s.trim().length === 0 ? i + 1 : null)
    .filter(n => n !== null);

  const handleSentenceChange = async (index, value) => {
    const newSentences = [...sentences];
    newSentences[index] = value;
    setSentences(newSentences);

    // Auto-save progress
    try {
      await saveExerciseProgress(topic, 'writing', {
        sentences: newSentences,
        showResults,
        evaluation,
        errorSentences,
        sentenceEvaluations
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const submitForEvaluation = async () => {
    if (!exerciseData) return;

    try {
      setIsEvaluating(true);
      setErrorSentences(null);

      // Keep previously-correct evaluations; only re-evaluate sentences that aren't already correct
      const results = { ...sentenceEvaluations };
      const indicesToEvaluate = sentences
        .map((_, i) => i)
        .filter(i => !results[i]?.is_correct);

      for (const i of indicesToEvaluate) {
        setEvaluatingIndex(i);

        const result = await evaluateWritingSentence({
          exercise_id: exerciseData.id,
          sentence: sentences[i].trim(),
          sentence_index: i,
          total_sentences: sentences.length
        });

        results[i] = result;
        setSentenceEvaluations(prev => ({ ...prev, [i]: result }));
      }

      setEvaluatingIndex(null);

      // Aggregate final score
      const correctCount = Object.values(results).filter(r => r.is_correct).length;
      const totalCount = sentences.length;
      const percentage = Math.round((correctCount / totalCount) * 1000) / 10;
      const sentencesWithErrors = Object.entries(results)
        .filter(([, r]) => !r.is_correct)
        .map(([idx]) => parseInt(idx) + 1);

      const aggregatedEvaluation = {
        score: correctCount,
        max_score: totalCount,
        percentage: percentage,
        is_acceptable: percentage >= 70,
        sentence_count: totalCount,
        sentences_with_errors: sentencesWithErrors,
        error_sentence_count: sentencesWithErrors.length,
      };

      setEvaluation(aggregatedEvaluation);
      setShowResults(true);

      // Stop timer
      const finalTime = stopTimer();

      // Record exercise attempt
      try {
        const result = await recordExerciseAttempt({
          topic: topic,
          exercise_type: 'writing',
          score: correctCount,
          max_score: totalCount,
          time_spent_seconds: finalTime || 0,
          errors: sentencesWithErrors.length > 0 ? [{
            count: sentencesWithErrors.length,
            sentences: sentencesWithErrors
          }] : null,
          exercise_ids: exerciseData?.id ? [exerciseData.id] : null
        });
        setEngagementData(result);
      } catch (trackingError) {
        console.error('Failed to record exercise attempt:', trackingError);
      }

      // Save progress with results
      await saveExerciseProgress(topic, 'writing', {
        sentences,
        showResults: true,
        evaluation: aggregatedEvaluation,
        sentenceEvaluations: results,
        errorSentences: null
      });

    } catch (err) {
      console.error('Error evaluating writing:', err);
      setError(err.message);
    } finally {
      setIsEvaluating(false);
      setEvaluatingIndex(null);
    }
  };

  const handleRestart = async () => {
    const errors = evaluation?.sentences_with_errors || [];
    // Preserve evaluations for previously-correct sentences so they aren't re-checked
    const preservedEvaluations = Object.fromEntries(
      Object.entries(sentenceEvaluations).filter(([, v]) => v?.is_correct)
    );
    setErrorSentences(errors);
    setEvaluation(null);
    setShowResults(false);
    setSentenceEvaluations(preservedEvaluations);
    setEvaluatingIndex(null);

    try {
      await saveExerciseProgress(topic, 'writing', {
        sentences,
        showResults: false,
        evaluation: null,
        sentenceEvaluations: preservedEvaluations,
        errorSentences: errors
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  if (loading) return <Loading />;
  if (error) return <Error message={error} />;

  // Results view
  if (showResults && evaluation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 p-4 md:p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Vyhodnocení
            </h1>
          </div>

          {/* Score Card */}
          <ExerciseCard className="mb-6">
            <div className="text-center mb-6">
              {/* Circular Progress */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={evaluation.percentage >= 70 ? '#22c55e' : '#ef4444'}
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${(evaluation.percentage / 100) * 351.86} 351.86`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-2xl font-bold ${evaluation.percentage >= 70 ? 'text-green-600' : 'text-red-500'}`}>
                    {evaluation.percentage}%
                  </span>
                </div>
              </div>

              <div className={`text-3xl font-bold ${evaluation.percentage >= 70 ? 'text-green-600' : 'text-red-500'}`}>
                {evaluation.score} / {evaluation.max_score}
              </div>
              <div className={`text-lg font-semibold mt-2 ${evaluation.is_acceptable ? 'text-green-600' : 'text-red-500'}`}>
                {evaluation.is_acceptable ? 'Přijatelné' : 'Potřebuje zlepšení'}
              </div>
              <div className="text-gray-500 mt-2">
                {evaluation.error_sentence_count > 0
                  ? `${evaluation.error_sentence_count} z ${evaluation.sentence_count} vět obsahuje chyby`
                  : `Všech ${evaluation.sentence_count} vět je správně!`
                }
              </div>

              {engagementData && (
                <XPBadge
                  xpEarned={engagementData.xp_earned}
                  totalXp={engagementData.total_xp}
                  level={engagementData.level}
                  levelName={engagementData.level_name}
                  levelUp={engagementData.level_up}
                />
              )}
            </div>

            {/* User Sentences with per-sentence feedback */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Vaše věty:</h3>
              <div className="space-y-2">
                {sentences.map((sentence, index) => {
                  const hasError = evaluation.sentences_with_errors?.includes(index + 1);
                  const sentenceResult = sentenceEvaluations[index];
                  return (
                    <div key={index}>
                      <div
                        className={`p-3 rounded-lg ${hasError ? 'bg-red-50 border-l-4 border-red-500' : 'bg-gray-50 border-l-4 border-green-500'}`}
                      >
                        <span className="font-semibold text-gray-500 mr-2">{index + 1}.</span>
                        <span className={hasError ? 'text-red-700' : 'text-gray-700'}>{sentence}</span>
                      </div>
                      {sentenceResult && !sentenceResult.is_correct && (
                        <div className="ml-4 mt-1 p-2 rounded bg-red-50 text-sm text-red-600">
                          <ReactMarkdown>{sentenceResult.feedback}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </ExerciseCard>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            <button
              onClick={handleRestart}
              className="py-3 px-8 rounded-lg font-semibold bg-white border-2 border-rose-500 text-rose-500 hover:bg-rose-50 transition-all"
            >
              Zkusit znovu
            </button>
            <button
              onClick={handleBack}
              className="py-3 px-8 rounded-lg font-semibold bg-rose-500 hover:bg-rose-600 text-white transition-all"
            >
              Zpět na menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main exercise view
  return (
    <ExerciseLayout
      title={exerciseData?.title || 'Psací cvičení'}
      subtitle={exerciseData?.instruction}
      color="rose"
      icon="✍️"
      onBack={handleBack}
      twoColumn={true}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left panel - Guiding questions (hidden for free-form assignments without them) */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          {exerciseData?.guiding_questions?.length > 0 && (
            <>
              <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">
                Naváděcí otázky
              </h5>
              <ExerciseCard>
                <ul className="list-disc pl-5 space-y-3">
                  {exerciseData.guiding_questions.map((question, idx) => (
                    <li key={idx} className="text-gray-600 leading-relaxed">
                      <TranslatableText text={question} textColor="#4b5563" topic={topic} />
                    </li>
                  ))}
                </ul>
              </ExerciseCard>
            </>
          )}

          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 text-center">
            <p className="text-sm text-green-700 font-medium">
              Pro dokončení cvičení potřebujete více než 50% správně.
            </p>
          </div>

          {exerciseData?.examples?.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowExamples(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm font-medium text-blue-700 transition-all"
              >
                <span>Příklady se stejnou strukturou</span>
                <span>{showExamples ? '▼' : '▶'}</span>
              </button>
              {showExamples && (
                <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex flex-col gap-3">
                    {exerciseData.examples.map((ex, i) => (
                      <div key={i} className="flex flex-col gap-1">
                        <div className="flex items-start gap-2">
                          <span className="text-xs text-gray-500 w-6 pt-0.5">CZ</span>
                          <div className="flex flex-wrap gap-1">
                            {ex.czech.split(' ').map((word, j) => (
                              <span key={j} className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{word}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-xs text-gray-500 w-6 pt-0.5">EN</span>
                          <div className="flex flex-wrap gap-1">
                            {ex.english.split(' ').map((word, j) => (
                              <span key={j} className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">{word}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right panel - Writing area */}
        <div className="lg:col-span-2">
          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">
            Vaše věty
          </h5>
          <ExerciseCard>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Napište {minSentences} vět:
              </h3>
              <div className={`text-sm font-semibold ${allSentencesFilled ? 'text-green-600' : 'text-red-500'}`}>
                Vyplněno: {filledCount} / {minSentences}
              </div>
            </div>

            <div className="space-y-3">
              {sentences.map((sentence, index) => (
                <div key={index} className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <label className="text-gray-500 font-semibold w-8 flex-shrink-0 text-right">
                      {index + 1}.
                    </label>
                    <input
                      type="text"
                      value={sentence}
                      onChange={(e) => handleSentenceChange(index, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && allSentencesFilled && !isEvaluating) {
                          e.preventDefault();
                          submitForEvaluation();
                        }
                      }}
                      disabled={isEvaluating || sentenceEvaluations[index]?.is_correct}
                      placeholder={`Věta ${index + 1}...`}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        sentenceEvaluations[index]
                          ? (sentenceEvaluations[index].is_correct
                              ? 'border-green-400 bg-green-50'
                              : 'border-red-400 bg-red-50')
                          : evaluatingIndex === index
                            ? 'border-yellow-400 bg-yellow-50 animate-pulse'
                            : errorSentences
                              ? (errorSentences.includes(index + 1)
                                  ? 'border-red-400 bg-red-50'
                                  : 'border-green-400 bg-green-50')
                              : (sentence.trim().length > 0
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-gray-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-200')
                      }`}
                    />
                    {evaluatingIndex === index && (
                      <span className="w-5 h-5 border-2 border-rose-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></span>
                    )}
                    {sentenceEvaluations[index] && (
                      <span className={`text-lg flex-shrink-0 ${sentenceEvaluations[index].is_correct ? 'text-green-600' : 'text-red-500'}`}>
                        {sentenceEvaluations[index].is_correct ? '\u2713' : '\u2717'}
                      </span>
                    )}
                  </div>
                  {sentenceEvaluations[index] && !sentenceEvaluations[index].is_correct && (
                    <div className="ml-11 p-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      <ReactMarkdown>{sentenceEvaluations[index].feedback}</ReactMarkdown>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center mt-6">
              <button
                onClick={handleBack}
                className="py-3 px-6 rounded-lg font-semibold bg-white border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-all"
              >
                Zpět
              </button>

              <button
                onClick={submitForEvaluation}
                disabled={!allSentencesFilled || isEvaluating}
                className={`py-3 px-8 rounded-lg font-semibold transition-all ${
                  allSentencesFilled && !isEvaluating
                    ? 'bg-rose-500 hover:bg-rose-600 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isEvaluating ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Vyhodnocuji větu {(evaluatingIndex ?? 0) + 1} z {minSentences}...
                  </span>
                ) : (
                  'Odeslat k vyhodnocení'
                )}
              </button>
            </div>

            {!allSentencesFilled && filledCount > 0 && (
              <p className="text-red-500 text-sm mt-3 text-right font-medium">
                Chybí věty: {emptySentenceNumbers.join(', ')}
              </p>
            )}
          </ExerciseCard>
        </div>
      </div>
    </ExerciseLayout>
  );
});

export default WritingExercise;
